import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Alert,
} from '@weave-mui/material';
import { RadioGroup, FormControlLabel } from '@mui/material';
import Radio from '@weave-mui/radio';
import Button, { buttonVariants } from '@weave-mui/button';
import Typography, { typographyVariants } from '@weave-mui/typography';
import LinearProgress, {
  linearProgressVariants,
} from '@weave-mui/linear-progress';
import { TrashM as TrashIcon } from '@weave-mui/icons-weave';
import { Info as InfoIcon } from '@mui/icons-material';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import { postGlobalFields } from '../../utils/api.service';
import { addNewNotification } from 'store/actions/notification_actions';
import { addGlobalFields } from '../../store/actions/hosting_actions';

/**
 * Validates that all keys in an array are unique
 * @param {Array} array - Array to validate
 * @param {string} [path] - Property path to extract the key from each item
 * @returns {boolean} True if all keys are unique, false otherwise
 */
const uniqueKeys = (array, path) => {
  const seen = new Set();
  if (!array) return true;
  return array.every((item) => {
    const key = path ? item[path] : item;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Creates validation schema for global field form
 * @param {Array} existingFields - Existing global fields to check for duplicates
 * @param {string} mode - Form mode ('create' or 'edit')
 * @param {string} originalKey - Original key when editing (to allow keeping same key)
 * @returns {Object} Yup validation schema
 */
const getValidationSchema = (
  existingFields = [],
  mode = 'create',
  originalKey = ''
) =>
  Yup.object().shape({
    key: Yup.string()
      .required('Key is required')
      .test(
        'unique-key',
        'A field with this key already exists',
        function (value) {
          if (!value) return true;
          // When editing, allow the same key as the original
          if (mode === 'edit' && value === originalKey) return true;
          // Check if key exists in other fields
          return !existingFields.some((field) => field.key === value);
        }
      ),
    type: Yup.string().required('Type is required'),
    value: Yup.string().when('type', {
      is: 'string',
      then: (schema) => schema.required('Value is required'),
    }),
    options: Yup.array().when('type', {
      is: 'choices',
      then: (schema) =>
        Yup.array()
          .of(
            Yup.object().shape({
              value: Yup.string().required('This value is required'),
            })
          )
          .min(1, 'At least one choice is required')
          .test('unique', 'Choices must be unique', function (options) {
            return uniqueKeys(options, 'value');
          }),
    }),
    default: Yup.string().when('type', {
      is: 'choices',
      then: (schema) => schema.required('Default value should be selected'),
    }),
  });

/**
 * GlobalFieldsModal - A modal for creating or editing a single global field
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} [props.onClose] - Callback function when modal is closed
 * @param {string} props.workstream - Workstream name
 * @param {string} props.org - Organization name
 * @param {string} [props.mode='create'] - Modal mode: 'create' or 'edit'
 * @param {Object} [props.fieldToEdit] - Field data when in edit mode
 */
const GlobalFieldsModal = ({
  isOpen,
  onClose = () => {},
  workstream,
  org,
  mode = 'create',
  fieldToEdit = null,
}) => {
  const globalFields = useSelector((state) => state.hosting.globalFields);
  const formikRef = useRef();
  const isMounted = useRef(true);
  const dispatch = useDispatch();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newChoiceValue, setNewChoiceValue] = useState('');

  // Initial values based on mode
  const getInitialValues = () => {
    if (mode === 'edit' && fieldToEdit) {
      return {
        key: fieldToEdit.key || '',
        type: fieldToEdit.type || 'string',
        value: fieldToEdit.value || '',
        options: fieldToEdit.options || [],
        default: fieldToEdit.default || '',
      };
    }
    // Create mode - empty form
    return {
      key: '',
      type: 'string',
      value: '',
      options: [],
      default: '',
    };
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setError(null);

      // Get existing fields from Redux
      const existingFields = globalFields.fields || [];

      let updatedFields;
      if (mode === 'edit') {
        // Replace the edited field
        updatedFields = existingFields.map((field) =>
          field.key === fieldToEdit.key ? values : field
        );
      } else {
        // Add new field
        updatedFields = [...existingFields, values];
      }

      const payload = { fields: updatedFields };

      const {
        data: { status, message },
      } = await postGlobalFields(org, workstream, payload);

      if (status === 'success') {
        dispatch(addNewNotification(message, 'success'));
        dispatch(addGlobalFields(payload));
        onClose();
      } else {
        setError(message);
        dispatch(addNewNotification(message, 'danger'));
      }
    } catch (error) {
      const errorDetail =
        error.response?.data?.detail || 'A network error occurred';
      setError(errorDetail);
      dispatch(addNewNotification(errorDetail, 'danger'));
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const modalTitle =
    mode === 'edit' ? 'Edit global field' : 'Create global field';

  // Get existing fields for validation (exclude the field being edited)
  const existingFields = globalFields.fields || [];
  const validationSchema = getValidationSchema(
    existingFields,
    mode,
    fieldToEdit?.key || ''
  );

  return (
    <Formik
      initialValues={getInitialValues()}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange={isSubmitted}
      validateOnBlur={isSubmitted}
      innerRef={formikRef}
    >
      {({ values, handleChange, errors, setFieldValue }) => {
        const actionButtons = (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              variant={buttonVariants.OUTLINED}
              onClick={onClose}
              disabled={loading}
              sx={{
                minWidth: '80px',
                height: '36px',
              }}
            >
              Cancel
            </Button>
            <Button
              variant={buttonVariants.CONTAINED}
              onClick={() => {
                if (formikRef.current) {
                  formikRef.current.submitForm();
                  setIsSubmitted(true);
                }
              }}
              disabled={loading}
              sx={{
                minWidth: '80px',
                height: '36px',
                backgroundColor: '#000000 !important',
                '&:hover': {
                  backgroundColor: '#333333 !important',
                },
              }}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        );

        return (
          <Modal
            open={isOpen}
            onClose={onClose}
            header={modalTitle}
            actions={actionButtons}
            sx={{
              '& .MuiModalWeave-box': {
                width: '600px !important',
                maxWidth: '95vw',
              },
              '& .MuiModalWeave-actions': {
                padding: '16px 24px',
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              },
              '& .MuiModalWeave-child.MuiBox-root': {
                padding: '24px',
              },
            }}
          >
            <Box>
              {/* Loading indicator */}
              {loading && (
                <LinearProgress
                  variant={linearProgressVariants.INDETERMINATE}
                  aria-label="Saving global fields"
                  sx={{ mb: 2 }}
                />
              )}

              {/* Error display */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Form>
                <Box>
                  {/* Key Field with Label */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant={typographyVariants.BODY}
                      sx={{ mb: 1, fontWeight: 500 }}
                    >
                      Key
                    </Typography>
                    <Field name="key">
                      {({ field: inputField }) => (
                        <TextField
                          {...inputField}
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="Enter key name"
                          error={!!errors.key}
                          helperText={errors.key}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '40px',
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  {/* Type Field with Label */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant={typographyVariants.BODY}
                      sx={{ mb: 1, fontWeight: 500 }}
                    >
                      Type
                    </Typography>
                    <RadioGroup
                      value={values.type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setFieldValue('type', newType);
                        // Reset value/options when switching type
                        if (newType === 'string') {
                          setFieldValue('value', '');
                          setFieldValue('options', []);
                          setFieldValue('default', '');
                        } else {
                          setFieldValue('value', '');
                          setFieldValue('options', []);
                          setFieldValue('default', '');
                        }
                      }}
                      row
                      sx={{ gap: 2 }}
                    >
                      <FormControlLabel
                        value="string"
                        control={<Radio />}
                        label={
                          <Typography variant={typographyVariants.BODY}>
                            String
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="choices"
                        control={<Radio />}
                        label={
                          <Typography variant={typographyVariants.BODY}>
                            Choices
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </Box>

                  {/* Value Field for String Type */}
                  {values.type === 'string' && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant={typographyVariants.BODY}
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        Value
                      </Typography>
                      <Field name="value">
                        {({ field: valueField }) => (
                          <TextField
                            {...valueField}
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder="Enter value"
                            error={!!errors.value}
                            helperText={errors.value}
                            sx={{
                              '& .MuiInputBase-root': {
                                height: '40px',
                              },
                            }}
                          />
                        )}
                      </Field>
                    </Box>
                  )}

                  {/* Choices Options */}
                  {values.type === 'choices' && (
                    <Box
                      sx={{
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '4px',
                        padding: '16px',
                        mb: 3,
                      }}
                    >
                      <FieldArray name="options">
                        {({ remove: removeOption, push: pushOption }) => (
                          <Box>
                            {/* Choice value label with info icon and input */}
                            <Box sx={{ mb: 2 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant={typographyVariants.BODY}
                                  sx={{ fontWeight: 500 }}
                                >
                                  Choice value
                                </Typography>
                                <Tooltip title="Enter choice values">
                                  <InfoIcon
                                    sx={{
                                      fontSize: '18px',
                                      color: 'text.secondary',
                                    }}
                                  />
                                </Tooltip>
                              </Box>

                              {/* Input + Add button row */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  alignItems: 'flex-start',
                                }}
                              >
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  placeholder="Value"
                                  value={newChoiceValue}
                                  onChange={(e) =>
                                    setNewChoiceValue(e.target.value)
                                  }
                                  onKeyPress={(e) => {
                                    if (
                                      e.key === 'Enter' &&
                                      newChoiceValue.trim()
                                    ) {
                                      e.preventDefault();
                                      pushOption({
                                        value: newChoiceValue.trim(),
                                      });
                                      setNewChoiceValue('');
                                    }
                                  }}
                                  sx={{
                                    '& .MuiInputBase-root': {
                                      height: '40px',
                                    },
                                  }}
                                />
                                <Button
                                  variant={buttonVariants.OUTLINED}
                                  onClick={() => {
                                    if (newChoiceValue.trim()) {
                                      pushOption({
                                        value: newChoiceValue.trim(),
                                      });
                                      setNewChoiceValue('');
                                    }
                                  }}
                                  disabled={!newChoiceValue.trim()}
                                  sx={{
                                    minWidth: '80px',
                                    height: '40px',
                                  }}
                                >
                                  Add
                                </Button>
                              </Box>
                            </Box>

                            {/* Title */}
                            <Typography
                              variant={typographyVariants.BODY}
                              sx={{ fontWeight: 600, mb: 2 }}
                            >
                              Manage choices
                            </Typography>

                            {/* Error Messages for choices */}
                            {errors.options &&
                              typeof errors.options === 'string' && (
                                <Typography
                                  variant={typographyVariants.CAPTION}
                                  color="error"
                                  sx={{ mb: 1 }}
                                >
                                  {errors.options}
                                </Typography>
                              )}
                            {errors.default && (
                              <Typography
                                variant={typographyVariants.CAPTION}
                                color="error"
                                sx={{ mb: 1 }}
                              >
                                {errors.default}
                              </Typography>
                            )}

                            {/* Options List or Empty State */}
                            {values.options.length > 0 ? (
                              <Box>
                                {values.options.map((option, optionIndex) => (
                                  <Box
                                    key={optionIndex}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 1,
                                      mb: 2,
                                    }}
                                  >
                                    {/* Option Input */}
                                    <Box sx={{ flex: 1 }}>
                                      <Field
                                        name={`options.${optionIndex}.value`}
                                      >
                                        {({ field: optionField }) => (
                                          <TextField
                                            {...optionField}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            placeholder={`Option ${
                                              optionIndex + 1
                                            }`}
                                            error={
                                              !!errors.options?.[optionIndex]
                                                ?.value
                                            }
                                            helperText={
                                              errors.options?.[optionIndex]
                                                ?.value
                                            }
                                            sx={{
                                              '& .MuiInputBase-root': {
                                                height: '40px',
                                              },
                                            }}
                                          />
                                        )}
                                      </Field>
                                    </Box>

                                    {/* Default Selection Button */}
                                    <Button
                                      variant={buttonVariants.TEXT}
                                      size="small"
                                      onClick={() => {
                                        setFieldValue('default', option.value);
                                      }}
                                      sx={{
                                        minWidth: '130px',
                                        height: '40px',
                                        textTransform: 'none',
                                        whiteSpace: 'nowrap',
                                        padding: '0 8px',
                                        color:
                                          values.default === option.value
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight:
                                          values.default === option.value
                                            ? 600
                                            : 400,
                                        '&:hover': {
                                          backgroundColor:
                                            'rgba(0, 0, 0, 0.04)',
                                          textDecoration: 'underline',
                                        },
                                      }}
                                    >
                                      <Typography
                                        variant={typographyVariants.BODY}
                                      >
                                        {values.default === option.value
                                          ? 'Default'
                                          : 'Set as default'}
                                      </Typography>
                                    </Button>

                                    {/* Delete Option Button */}
                                    <Tooltip title="Delete option">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          if (values.default === option.value) {
                                            setFieldValue('default', '');
                                          }
                                          removeOption(optionIndex);
                                        }}
                                        sx={{
                                          width: '40px',
                                          height: '40px',
                                          color: 'text.secondary',
                                          '&:hover': {
                                            color: 'error.main',
                                            backgroundColor:
                                              'rgba(211, 47, 47, 0.08)',
                                          },
                                        }}
                                      >
                                        <TrashIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              /* Empty State Placeholder */
                              <Box
                                sx={{
                                  border: '2px dashed rgba(0, 0, 0, 0.23)',
                                  borderRadius: '8px',
                                  padding: '48px 24px',
                                  textAlign: 'center',
                                  backgroundColor: '#FAFAFA',
                                }}
                              >
                                <Typography
                                  variant={typographyVariants.BODY}
                                  sx={{
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    mb: 1,
                                  }}
                                >
                                  No choices added yet.
                                </Typography>
                                <Typography
                                  variant={typographyVariants.BODY}
                                  sx={{ color: 'text.secondary' }}
                                >
                                  Add at least one choice to continue.
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </FieldArray>
                    </Box>
                  )}
                </Box>
              </Form>
            </Box>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default GlobalFieldsModal;
